import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './AuthContext'
import { useOrg } from './OrgContext'
import type {
  Workflow,
  WorkflowGraph,
  WorkflowRun,
  WorkflowEventLog,
  WorkflowTriggerType,
} from '@/types/workflow'

// ────────────────────────────────────────────────────────────────────────
// deriveTriggerFromGraph (CA-015)
// The executor's scheduler (process-workflow STEP 0) selects workflows by the
// COLUMNS workflows.trigger_type='scheduled' + trigger_config.cron — NOT by the
// graph. Previously createWorkflow hardcoded trigger_type='manual' and saveGraph
// only wrote graph_json, so a workflow whose trigger node was "Scheduled" never
// fired: the columns stayed manual/{}. This reads the trigger node from the
// graph and returns the column values that keep the two in sync on every save.
// ────────────────────────────────────────────────────────────────────────
function deriveTriggerFromGraph(graph: WorkflowGraph): {
  trigger_type: WorkflowTriggerType
  trigger_config: Record<string, unknown>
} {
  const triggerNode = graph.nodes.find((n) => n.type?.startsWith('trigger_'))
  const t = triggerNode?.type

  if (t === 'trigger_scheduled') {
    const d = (triggerNode?.data || {}) as Record<string, unknown>
    return {
      trigger_type: 'scheduled',
      // Only carry the schedule fields the executor reads. Preserve any
      // last_triggered_at that a prior run may have stamped is handled at the
      // call site (we merge, not clobber).
      trigger_config: {
        cron: (d.cron as string) || '',
        timezone: (d.timezone as string) || 'America/Mexico_City',
        description: (d.description as string) || '',
      },
    }
  }

  if (t === 'trigger_new_lead') {
    return { trigger_type: 'new_lead_added', trigger_config: {} }
  }

  // trigger_manual or anything else → manual (default, safe).
  return { trigger_type: 'manual', trigger_config: {} }
}

interface WorkflowContextType {
  workflows: Workflow[]
  isLoading: boolean
  createWorkflow: (name: string, workflowType?: 'lead' | 'agent') => Promise<Workflow | null>
  updateWorkflow: (id: string, data: Partial<Workflow>) => Promise<void>
  deleteWorkflow: (id: string) => Promise<void>
  saveGraph: (id: string, graphJson: WorkflowGraph) => Promise<void>
  activateWorkflow: (id: string) => Promise<void>
  pauseWorkflow: (id: string) => Promise<void>
  getWorkflowRuns: (workflowId: string) => WorkflowRun[]
  getWorkflowEventLog: (runId: string) => WorkflowEventLog[]
  eventLogTruncated: boolean
  enrollLeads: (workflowId: string, leadIds: string[]) => Promise<{ enrolled: number; skipped: number }>
  // Run lifecycle controls (CA-013). workflow_runs has an org-scoped UPDATE RLS
  // policy (mig 027 line 720, left intact by mig 161) so org members can UPDATE
  // status directly — no edge function required.
  cancelRun: (runId: string) => Promise<void>
  retryRun: (runId: string) => Promise<void>
  cancelAllActiveRuns: (workflowId: string) => Promise<number>
  isRunActionPending: boolean
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined)

export function WorkflowProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { orgId } = useOrg()
  const queryClient = useQueryClient()

  const { data: workflows = [], isLoading } = useQuery({
    queryKey: ['workflows', orgId],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data || []) as Workflow[]
    },
    enabled: !!user && !!orgId,
  })

  const createWorkflowMutation = useMutation({
    mutationFn: async ({ name, workflowType = 'lead' }: { name: string; workflowType?: 'lead' | 'agent' }) => {
      if (!user || !orgId) throw new Error('Not authenticated')

      const defaultGraph: WorkflowGraph = {
        nodes: [
          {
            id: 'trigger-1',
            type: 'trigger_manual',
            position: { x: 250, y: 50 },
            data: { label: 'Manual Trigger' },
          },
        ],
        edges: [],
      }

      // CA-015: keep trigger columns consistent with the graph from creation.
      const { trigger_type, trigger_config } = deriveTriggerFromGraph(defaultGraph)

      const { data, error } = await supabase
        .from('workflows')
        .insert({
          name,
          owner_id: user.id,
          org_id: orgId,
          status: 'draft',
          graph_json: defaultGraph,
          trigger_type,
          trigger_config,
          workflow_type: workflowType,
        })
        .select()
        .single()
      if (error) throw error
      return data as Workflow
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflows'] }),
  })

  const updateWorkflowMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Workflow> }) => {
      const { error } = await supabase.from('workflows').update(data).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflows'] }),
  })

  const deleteWorkflowMutation = useMutation({
    mutationFn: async (id: string) => {
      // CA-042: mark active runs 'failed' BEFORE deleting the workflow. Deleting
      // cascade-drops workflow_runs, but agent_tasks_v2.workflow_run_id has no FK
      // (mig 091 only ADD COLUMN) so already-claimed tasks are NOT cancelled and
      // the agent still executes them (orphan LLM spend). Failing the runs first
      // makes the advance trigger a no-op and stops any further task creation.
      // NOTE: fully cancelling in-flight agent tasks requires a service-role
      // 'delete-workflow' edge fn — the frontend has SELECT-only RLS on
      // agent_tasks_v2 (mig 079/161) and cannot UPDATE it from the client.
      const { error: runErr } = await supabase
        .from('workflow_runs')
        .update({ status: 'failed', waiting_until: null, waiting_for_event: null })
        .eq('workflow_id', id)
        .in('status', ['running', 'waiting', 'paused'])
      if (runErr) throw runErr

      const { error } = await supabase.from('workflows').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['workflow-runs'] })
    },
  })

  const saveGraphMutation = useMutation({
    mutationFn: async ({ id, graphJson }: { id: string; graphJson: WorkflowGraph }) => {
      // CA-015: derive trigger_type/trigger_config from the graph's trigger node
      // and persist them to the columns the executor actually reads. Preserve
      // last_triggered_at (stamped by the scheduler) so saving the graph does not
      // reset the schedule window.
      const existing = workflows.find((w) => w.id === id)
      // trigger_config is a DB column (mig 091) not yet on the Workflow type.
      const prevConfig =
        ((existing as unknown as { trigger_config?: Record<string, unknown> } | undefined)
          ?.trigger_config) || {}
      const { trigger_type, trigger_config } = deriveTriggerFromGraph(graphJson)
      const mergedConfig: Record<string, unknown> = { ...trigger_config }
      if (trigger_type === 'scheduled' && prevConfig.last_triggered_at) {
        mergedConfig.last_triggered_at = prevConfig.last_triggered_at
      }

      const { error } = await supabase
        .from('workflows')
        .update({ graph_json: graphJson, trigger_type, trigger_config: mergedConfig })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflows'] }),
  })

  const enrollLeadsMutation = useMutation({
    mutationFn: async ({ workflowId, leadIds }: { workflowId: string; leadIds: string[] }) => {
      if (!user || !orgId) throw new Error('Not authenticated')

      const workflow = workflows.find((w) => w.id === workflowId)
      if (!workflow) throw new Error('Workflow not found')

      // CA-031: only enroll into an active workflow. Runs created on a
      // draft/paused workflow become zombie 'running' rows the executor loops on
      // ("Workflow not active") until a lifecycle guard fails them.
      if (workflow.status !== 'active') {
        throw new Error('Activa el workflow antes de asignar leads')
      }

      const triggerNode = workflow.graph_json.nodes.find((n) =>
        n.type?.startsWith('trigger_')
      )
      if (!triggerNode) throw new Error('Workflow has no trigger node')

      // Find the first node after the trigger
      const outgoingEdge = workflow.graph_json.edges.find(
        (e) => e.source === triggerNode.id
      )
      const firstNodeId = outgoingEdge?.target || triggerNode.id

      // CA-031: never reset progress of already-enrolled leads. The old upsert
      // onConflict(workflow_id,lead_id) rewound a mid-sequence lead back to the
      // first node → repeated LinkedIn actions to the same person. Skip any lead
      // that already has a run for this workflow; only INSERT genuinely new ones.
      const { data: existing, error: existingErr } = await supabase
        .from('workflow_runs')
        .select('lead_id')
        .eq('workflow_id', workflowId)
        .in('lead_id', leadIds)
      if (existingErr) throw existingErr

      const alreadyEnrolled = new Set((existing || []).map((r) => r.lead_id))
      const newLeadIds = leadIds.filter((id) => !alreadyEnrolled.has(id))
      const skipped = leadIds.length - newLeadIds.length

      if (newLeadIds.length === 0) {
        return { enrolled: 0, skipped }
      }

      const runs = newLeadIds.map((leadId) => ({
        workflow_id: workflowId,
        lead_id: leadId,
        owner_id: user.id,
        org_id: orgId,
        current_node_id: firstNodeId,
        status: 'running' as const,
      }))

      // Plain INSERT (not upsert) — existing runs were already filtered out, so
      // there is no conflict to resolve and no path that rewinds progress.
      const { error } = await supabase.from('workflow_runs').insert(runs)
      if (error) throw error
      return { enrolled: newLeadIds.length, skipped }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-runs'] })
    },
  })

  // Workflow runs query — returns cached data for a workflow.
  // CA-013: poll so a running/zombie run's status is not shown stale. The window
  // only refetches while mounted, so this is bounded to when someone is looking.
  const { data: allRuns = [] } = useQuery({
    queryKey: ['workflow-runs', orgId],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('workflow_runs')
        .select('*')
        .eq('org_id', orgId)
        .order('started_at', { ascending: false })
      if (error) throw error
      return (data || []) as WorkflowRun[]
    },
    enabled: !!user && !!orgId,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  })

  const EVENT_LOG_LIMIT = 500
  const { data: allEventLogs = [] } = useQuery({
    queryKey: ['workflow-event-log', orgId],
    queryFn: async () => {
      if (!user || !orgId) return []
      const { data, error } = await supabase
        .from('workflow_event_log')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(EVENT_LOG_LIMIT)
      if (error) throw error
      return (data || []) as WorkflowEventLog[]
    },
    enabled: !!user && !!orgId,
    refetchInterval: 15000,
  })

  // CA-053: the org-wide query is capped at EVENT_LOG_LIMIT ordered newest-first.
  // If we hit the cap, older runs' events may be missing — expose that so the UI
  // can show a truncation hint instead of a silently-empty/incomplete timeline.
  const eventLogTruncated = allEventLogs.length >= EVENT_LOG_LIMIT

  // ── Run lifecycle mutations (CA-013) ──────────────────────────────────────
  // Direct workflow_runs UPDATEs, authorized by the org-scoped UPDATE policy
  // (mig 027 line 720, preserved by mig 161). Cancelling = mark 'failed' with a
  // reason; the tick query never re-selects a 'failed' run so the zombie/runaway
  // stops. We deliberately do NOT touch agent_tasks_v2 here: the frontend has
  // SELECT-only RLS on it (mig 079/161), so a client-side task cancel would
  // silently no-op. Fully cancelling in-flight agent tasks needs a service-role
  // edge fn (see CA-042 note).
  const cancelRunMutation = useMutation({
    mutationFn: async (runId: string) => {
      const run = allRuns.find((r) => r.id === runId)
      const prevContext = (run?.context_json as Record<string, unknown>) || {}
      const { error } = await supabase
        .from('workflow_runs')
        .update({
          status: 'failed',
          waiting_until: null,
          waiting_for_event: null,
          context_json: { ...prevContext, last_error: 'Cancelled by user' },
        })
        .eq('id', runId)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflow-runs'] }),
  })

  const retryRunMutation = useMutation({
    mutationFn: async (runId: string) => {
      // Re-arm a failed/paused run from its current node. Clear any wait state
      // and the last_error marker so the next tick executes the current node.
      const run = allRuns.find((r) => r.id === runId)
      const prevContext = { ...((run?.context_json as Record<string, unknown>) || {}) }
      delete prevContext.last_error
      const { error } = await supabase
        .from('workflow_runs')
        .update({
          status: 'running',
          waiting_until: null,
          waiting_for_event: null,
          context_json: prevContext,
        })
        .eq('id', runId)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflow-runs'] }),
  })

  const cancelAllActiveRunsMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      // Cancel every non-terminal run of this workflow in one statement. The
      // .in() filter on status keeps us from touching completed/failed rows.
      const { data, error } = await supabase
        .from('workflow_runs')
        .update({
          status: 'failed',
          waiting_until: null,
          waiting_for_event: null,
        })
        .eq('workflow_id', workflowId)
        .in('status', ['running', 'waiting', 'paused'])
        .select('id')
      if (error) throw error
      return (data || []).length
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workflow-runs'] }),
  })

  const isRunActionPending =
    cancelRunMutation.isPending ||
    retryRunMutation.isPending ||
    cancelAllActiveRunsMutation.isPending

  return (
    <WorkflowContext.Provider
      value={{
        workflows,
        isLoading,
        createWorkflow: async (name, workflowType) => createWorkflowMutation.mutateAsync({ name, workflowType }),
        updateWorkflow: async (id, data) => updateWorkflowMutation.mutateAsync({ id, data }),
        deleteWorkflow: async (id) => deleteWorkflowMutation.mutateAsync(id),
        saveGraph: async (id, graphJson) => saveGraphMutation.mutateAsync({ id, graphJson }),
        activateWorkflow: async (id) =>
          updateWorkflowMutation.mutateAsync({ id, data: { status: 'active' } }),
        pauseWorkflow: async (id) =>
          updateWorkflowMutation.mutateAsync({ id, data: { status: 'paused' } }),
        getWorkflowRuns: (workflowId) =>
          allRuns.filter((r) => r.workflow_id === workflowId),
        getWorkflowEventLog: (runId) =>
          // CA-053: the source query is created_at DESC (newest first). Reverse
          // per-run so the Steps timeline reads in execution order (oldest→newest).
          allEventLogs
            .filter((e) => e.workflow_run_id === runId)
            .slice()
            .reverse(),
        eventLogTruncated,
        enrollLeads: async (workflowId, leadIds) =>
          enrollLeadsMutation.mutateAsync({ workflowId, leadIds }),
        cancelRun: async (runId) => cancelRunMutation.mutateAsync(runId),
        retryRun: async (runId) => retryRunMutation.mutateAsync(runId),
        cancelAllActiveRuns: async (workflowId) =>
          cancelAllActiveRunsMutation.mutateAsync(workflowId),
        isRunActionPending,
      }}
    >
      {children}
    </WorkflowContext.Provider>
  )
}

export function useWorkflow() {
  const context = useContext(WorkflowContext)
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider')
  }
  return context
}
